import { useState } from 'react';

// ─── AccountView ──────────────────────────────────────────────────────────────
// Props:
//   user          — { id, email, is_subscribed, has_password, created_at, subscribed_at, listings_count }
//   token         — JWT string
//   onLogout      — () => void
//   onUserUpdated — (updatedUser) => void
export default function AccountView({ user, token, onLogout, onUserUpdated }) {
  return (
    <div className="av">
      <ProfileSection user={user} />
      <SubscriptionSection user={user} token={token} />
      <PromoCodeSection user={user} token={token} onUserUpdated={onUserUpdated} />
      <ChangePasswordSection token={token} user={user} />
      <SignInMethodSection user={user} />
      <SupportSection />
      <DangerSection onLogout={onLogout} />

      <style>{`
        .av { display: flex; flex-direction: column; gap: 0; padding-bottom: 32px; }

        .av-section { padding: 20px 0; border-bottom: 0.5px solid var(--color-border); }
        .av-section:last-child { border-bottom: none; }

        .av-section-title {
          font-size: 11px; font-weight: 500; letter-spacing: 0.07em;
          text-transform: uppercase; color: var(--color-text-hint); margin-bottom: 14px;
        }

        .av-card {
          background: var(--color-bg-card); border: 0.5px solid var(--color-border);
          border-radius: var(--radius-lg); overflow: hidden;
        }

        .av-row {
          display: flex; align-items: center; justify-content: space-between;
          padding: 14px 16px; border-bottom: 0.5px solid var(--color-border); gap: 12px;
        }
        .av-row:last-child { border-bottom: none; }
        .av-row-label { font-size: 13px; color: var(--color-text-secondary); }
        .av-row-value { font-size: 13px; font-weight: 500; color: var(--color-text-heading); text-align: right; word-break: break-all; }

        .av-badge {
          font-size: 10px; font-weight: 500; letter-spacing: 0.05em;
          text-transform: uppercase; padding: 3px 9px; border-radius: 9999px;
        }
        .av-badge--pro { background: var(--color-primary-light); color: var(--color-primary); }
        .av-badge--free { background: var(--color-bg-page); color: var(--color-text-hint); border: 0.5px solid var(--color-border); }

        .av-form { display: flex; flex-direction: column; gap: 12px; }
        .av-field { display: flex; flex-direction: column; gap: 6px; }
        .av-label { font-size: 12px; font-weight: 500; color: var(--color-text-secondary); }

        .av-input {
          width: 100%; padding: 11px 14px;
          background: var(--color-bg-input); border: 0.5px solid var(--color-border);
          border-radius: var(--radius-md); color: var(--color-text-heading);
          font-family: inherit; font-size: 16px; outline: none; transition: border-color 0.15s, box-shadow 0.15s;
        }
        .av-input::placeholder { color: var(--color-text-hint); }
        .av-input:focus { border-color: var(--color-primary); box-shadow: 0 0 0 3px var(--color-focus-ring); }
        .av-input:disabled { opacity: 0.45; }

        .av-btn {
          width: 100%; height: 44px; padding: 0 16px; border-radius: var(--radius-md);
          font-family: inherit; font-size: 14px; font-weight: 500; cursor: pointer;
          transition: background 0.15s, transform 0.1s; -webkit-tap-highlight-color: transparent; border: none;
        }
        .av-btn:active { transform: scale(0.985); }
        .av-btn:disabled { opacity: 0.35; pointer-events: none; }

        .av-btn--primary { background: var(--color-primary); color: #EAF3DE; }
        .av-btn--primary:hover { background: var(--color-primary-hover); }
        .av-btn--ghost { background: var(--color-bg-page); color: var(--color-text-secondary); border: 0.5px solid var(--color-border-dashed); }
        .av-btn--danger { background: rgba(220,38,38,0.07); color: #DC2626; border: 0.5px solid rgba(220,38,38,0.2); }

        .av-success { font-size: 13px; color: var(--success); text-align: center; padding: 8px 0 0; }
        .av-error {
          font-size: 13px; color: #DC2626; padding: 8px 12px;
          background: rgba(220,38,38,0.06); border: 0.5px solid rgba(220,38,38,0.2); border-radius: var(--radius-sm);
        }

        .av-social-icon { width: 20px; height: 20px; flex-shrink: 0; }

        .av-hint { font-size: 12px; color: var(--color-text-hint); line-height: 1.6; margin-top: 10px; }
      `}</style>
    </div>
  );
}

// ─── Date formatting helper ─────────────────────────────────────────────────
function formatDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

// ─── Profile Section ──────────────────────────────────────────────────────────
function ProfileSection({ user }) {
  const memberSince = formatDate(user?.created_at);
  return (
    <section className="av-section">
      <h2 className="av-section-title">Account</h2>
      <div className="av-card">
        <div className="av-row">
          <span className="av-row-label">Email</span>
          <span className="av-row-value">{user?.email}</span>
        </div>
        <div className="av-row">
          <span className="av-row-label">Listings generated</span>
          <span className="av-row-value">{user?.listings_count ?? 0}</span>
        </div>
        {memberSince && (
          <div className="av-row">
            <span className="av-row-label">Member since</span>
            <span className="av-row-value">{memberSince}</span>
          </div>
        )}
      </div>
    </section>
  );
}

// ─── Promo access helper ─────────────────────────────────────────────────────
function promoAccessActive(user) {
  return !!(user?.promo_access_until && new Date(user.promo_access_until) > new Date());
}

// ─── Subscription Section ─────────────────────────────────────────────────────
function SubscriptionSection({ user, token }) {
  const isSubscribed = user?.is_subscribed ?? false;
  const promoActive = promoAccessActive(user);
  const proSince = formatDate(user?.subscribed_at);

  async function handleManage() {
    try {
      const res = await fetch('/stripe/portal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch {
      alert('Could not open billing portal. Please try again.');
    }
  }

  return (
    <section className="av-section">
      <h2 className="av-section-title">Subscription</h2>
      <div className="av-card">
        <div className="av-row">
          <span className="av-row-label">Plan</span>
          <span className={`av-badge ${isSubscribed || promoActive ? 'av-badge--pro' : 'av-badge--free'}`}>
            {isSubscribed ? 'Pro' : promoActive ? 'Tester' : 'Free'}
          </span>
        </div>
        {(isSubscribed || promoActive) && (
          <div className="av-row">
            <span className="av-row-label">Free generations left</span>
            <span className="av-row-value">Unlimited</span>
          </div>
        )}
        {isSubscribed && proSince && (
          <div className="av-row">
            <span className="av-row-label">Pro since</span>
            <span className="av-row-value">{proSince}</span>
          </div>
        )}
        {!isSubscribed && promoActive && (
          <div className="av-row">
            <span className="av-row-label">Access until</span>
            <span className="av-row-value">{formatDate(user.promo_access_until)}</span>
          </div>
        )}
        {!isSubscribed && !promoActive && (
          <div className="av-row">
            <span className="av-row-label">Free generations left</span>
            <span className="av-row-value">{Math.max(0, 3 - (user?.listings_count ?? 0))} of 3</span>
          </div>
        )}
      </div>
      {isSubscribed && (
        <div style={{ marginTop: 10 }}>
          <button className="av-btn av-btn--ghost" onClick={handleManage}>
            Manage Subscription
          </button>
          <p className="av-hint">Opens Stripe billing portal to cancel, pause, or update payment details.</p>
        </div>
      )}
    </section>
  );
}

// ─── Promo Code ───────────────────────────────────────────────────────────────
function PromoCodeSection({ user, token, onUserUpdated }) {
  const [code,    setCode]    = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error,   setError]   = useState(null);

  const promoActive = promoAccessActive(user);
  const accessUntil = formatDate(user?.promo_access_until);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (!code.trim()) return;

    setLoading(true);
    try {
      const res = await fetch('/promo/redeem', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ code: code.trim() }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error?.message || 'Could not apply the code.');
        return;
      }
      setSuccess(`Code applied — full access until ${formatDate(data.data.accessUntil)}.`);
      setCode('');
      onUserUpdated?.({ ...user, promo_access_until: data.data.accessUntil });
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="av-section">
      <h2 className="av-section-title">Promo Code</h2>

      {promoActive && !success && (
        <p className="av-hint" style={{ marginTop: 0, marginBottom: 12 }}>
          ✓ Tester access active until {accessUntil}. You can apply another code to extend it.
        </p>
      )}

      <form className="av-form" onSubmit={handleSubmit} noValidate>
        <div className="av-field">
          <label className="av-label" htmlFor="promo-code">Have an access code?</label>
          <input
            id="promo-code"
            className="av-input"
            type="text"
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck="false"
            placeholder="e.g. TESTER30"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            disabled={loading}
          />
        </div>

        {error   && <div className="av-error">{error}</div>}
        {success && <p className="av-success">✓ {success}</p>}

        <button className="av-btn av-btn--ghost" type="submit" disabled={loading || !code.trim()}>
          {loading ? 'Applying…' : 'Apply Code'}
        </button>
      </form>
    </section>
  );
}

// ─── Change Password ──────────────────────────────────────────────────────────
function ChangePasswordSection({ token, user }) {
  const [current,  setCurrent]  = useState('');
  const [next,     setNext]     = useState('');
  const [confirm,  setConfirm]  = useState('');
  const [loading,  setLoading]  = useState(false);
  const [success,  setSuccess]  = useState(false);
  const [error,    setError]    = useState(null);

  // Social-login accounts have no password to change
  const hasSocialAccount = user && !user.has_password;

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    if (next !== confirm) { setError('New passwords do not match.'); return; }
    if (next.length < 8)  { setError('New password must be at least 8 characters.'); return; }

    setLoading(true);
    try {
      const res = await fetch('/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ currentPassword: current, newPassword: next }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error?.message || 'Password change failed.'); return; }
      setSuccess(true);
      setCurrent(''); setNext(''); setConfirm('');
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="av-section">
      <h2 className="av-section-title">Change Password</h2>

      {hasSocialAccount ? (
        <p className="av-hint">You signed in with Google — no password to change.</p>
      ) : (
        <form className="av-form" onSubmit={handleSubmit} noValidate>
          <div className="av-field">
            <label className="av-label" htmlFor="cp-current">Current password</label>
            <input id="cp-current" className="av-input" type="password" autoComplete="current-password"
              placeholder="••••••••" value={current} onChange={(e) => setCurrent(e.target.value)} disabled={loading} />
          </div>
          <div className="av-field">
            <label className="av-label" htmlFor="cp-new">New password</label>
            <input id="cp-new" className="av-input" type="password" autoComplete="new-password"
              placeholder="Min. 8 characters" value={next} onChange={(e) => setNext(e.target.value)} disabled={loading} />
          </div>
          <div className="av-field">
            <label className="av-label" htmlFor="cp-confirm">Confirm new password</label>
            <input id="cp-confirm" className="av-input" type="password" autoComplete="new-password"
              placeholder="••••••••" value={confirm} onChange={(e) => setConfirm(e.target.value)} disabled={loading} />
          </div>

          {error   && <div className="av-error">{error}</div>}
          {success && <p className="av-success">✓ Password updated successfully.</p>}

          <button className="av-btn av-btn--primary" type="submit"
            disabled={loading || !current || !next || !confirm}>
            {loading ? 'Updating…' : 'Update Password'}
          </button>
        </form>
      )}
    </section>
  );
}

// ─── Sign-in Method ───────────────────────────────────────────────────────────
function SignInMethodSection({ user }) {
  const usesGoogle = user && !user.has_password;
  return (
    <section className="av-section">
      <h2 className="av-section-title">Sign-in Method</h2>
      <div className="av-card">
        <div className="av-row">
          <span className="av-row-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {usesGoogle ? <GoogleIcon /> : null}
            {usesGoogle ? 'Google' : 'Email & Password'}
          </span>
        </div>
      </div>
    </section>
  );
}

// ─── Support ──────────────────────────────────────────────────────────────────
function SupportSection() {
  return (
    <section className="av-section">
      <h2 className="av-section-title">Support</h2>
      <div className="av-card">
        <div className="av-row">
          <span className="av-row-label">Need help?</span>
          <a className="av-row-value" href="mailto:support@sold4you.app" style={{ color: 'var(--color-primary)' }}>
            support@sold4you.app
          </a>
        </div>
      </div>
    </section>
  );
}

// ─── Danger Zone ─────────────────────────────────────────────────────────────
function DangerSection({ onLogout }) {
  return (
    <section className="av-section">
      <button className="av-btn av-btn--danger" onClick={onLogout}>
        Sign Out
      </button>
    </section>
  );
}

// ─── SVG Icons ────────────────────────────────────────────────────────────────
function GoogleIcon() {
  return (
    <svg className="av-social-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}
